import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Mail, Send, Loader2, AlertCircle } from "lucide-react";

/**
 * Mail composer dialog.
 * Props:
 *   open, onOpenChange — control
 *   request           — the full request object (must have items, request_no, supplier_*)
 *   onSent?           — callback after send/share success
 */
export default function MailComposeDialog({ open, onOpenChange, request, onSent }) {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  // Preload supplier list when dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        setSuppliers(data);
        // If the request's supplier has no email on the request object, derive it from the loaded list
        if (request?.supplier_id && !request?.supplier_email) {
          const s = data.find((x) => x.id === request.supplier_id);
          if (s?.email) setTo(s.email);
        }
      } catch (_) {}
    })();
    // Initialize with the request's own supplier as default
    if (request) {
      setSupplierId(request.supplier_id || "");
      setTo(request.supplier_email || "");
      setSubject(`Sipariş Talebi: ${request.request_no}`);
      const items = ((request.items && request.items.length > 0) ? request.items : [{
        product_code: request.product_code, product_name: request.product_name,
        quantity: request.quantity, unit: request.unit,
      }]).filter((it) => !it.unavailable);
      const list = items.map((it, i) => `${i + 1}. ${it.product_code} — ${it.product_name} • ${it.quantity} ${it.unit}${it.note ? `\n   Not: ${it.note}` : ""}`).join("\n");
      setBody(
        `Merhaba ${request.supplier_name || ""},\n\n` +
        `${request.request_no} numaralı sipariş talebimizi ekteki PDF'te bulabilirsiniz.\n\n` +
        `Ürünler:\n${list}\n\n` +
        `Bilgilerinize sunarız, iyi çalışmalar dileriz.\n\n` +
        `Talep Eden: ${user?.name || ""}` + (user?.email ? ` <${user.email}>` : "")
      );
    }
  }, [open, request, user?.name, user?.email]);

  // When user picks a different supplier from the dropdown, update To: and supplier name in body
  const onSupplierChange = (v) => {
    setSupplierId(v);
    const s = suppliers.find((x) => x.id === v);
    if (s) {
      setTo(s.email || "");
      // Update the greeting line of the body if it still references the previous supplier
      setBody((prev) => prev.replace(/^Merhaba [^,]*,/, `Merhaba ${s.name},`));
    }
  };

  const fetchPdfBlob = async () => {
    const sid = supplierId || request.supplier_id || "";
    const { data } = await api.get(`/requests/${request.id}/pdf${sid ? `?supplier_id=${sid}` : ""}`, { responseType: "blob" });
    return new File([data], `talep-${request.request_no}.pdf`, { type: "application/pdf" });
  };

  // Open the default desktop mail composer (Outlook / Apple Mail) in a NEW mail window.
  // We download the PDF first so the user can attach it; mailto: opens a fresh compose
  // window where the user's own signature is applied automatically.
  const handleOpenMail = async () => {
    if (!to) { toast.error("Alıcı e-posta adresi gerekli"); return; }
    setBusy(true);
    const cc = user?.email || "";
    try {
      // Download the PDF so the user can attach it to the mail they are about to compose
      try {
        const file = await fetchPdfBlob();
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (_) {}

      // Open a NEW mail window in the default desktop client (Outlook/Mail)
      const params = new URLSearchParams();
      params.set("subject", subject);
      params.set("body", body);
      if (cc) params.set("cc", cc);
      const mailUrl = `mailto:${encodeURIComponent(to)}?${params.toString().replace(/\+/g, "%20")}`;
      const link = document.createElement("a");
      link.href = mailUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Record the send + notify the request owner which supplier it went to
      const supplierName = suppliers.find((s) => s.id === supplierId)?.name || request.supplier_name || "";
      try {
        await api.post(`/requests/${request.id}/mark-sent`, {
          channel: "mail",
          supplier_id: supplierId || request.supplier_id,
          supplier_name: supplierName,
        });
      } catch (_) {}

      toast.success("Mail penceresi açıldı — indirilen PDF'i ekleyip gönderin");
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Mail penceresi açılamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mail-compose-dialog" className="max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Mail className="h-5 w-5 text-blue-600" /> Mail ile Gönder
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Tedarikçi seçin ve onaylayın — varsayılan mail pencereniz imzanızla açılır, PDF indirilir ve ekleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={onSupplierChange}>
              <SelectTrigger data-testid="mail-supplier-select" className="mt-1.5 h-11">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`mail-supplier-${s.id}`}>
                    {s.name}
                    {s.email && <span className="text-slate-400 ml-1">· {s.email}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Alıcı (To)</Label>
            <Input
              data-testid="mail-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1.5 h-11"
              placeholder="tedarikci@firma.com"
            />
            {!to && (
              <p className="text-xs text-amber-600 mt-1 inline-flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Bu tedarikçide e-posta kayıtlı değil — alıcıyı manuel girin.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">CC (Gönderen)</Label>
            <div className="mt-1.5 h-11 px-3 flex items-center bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">
              {user?.email || "—"} <span className="text-slate-400 ml-1">({user?.name})</span>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Konu</Label>
            <Input
              data-testid="mail-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1.5 h-11"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Mesaj</Label>
            <Textarea
              data-testid="mail-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              className="mt-1.5 text-sm font-mono"
            />
          </div>

          <div className="text-xs bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-2.5">
            💡 <strong>Bilgi:</strong> "Mail'i Aç" ile varsayılan mail pencereniz (Outlook/Mail) imzanızla birlikte açılır.
            PDF otomatik indirilir — açılan maile ekleyip gönderin.
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="w-full sm:w-auto">
            Vazgeç
          </Button>
          <Button
            data-testid="mail-open-btn"
            onClick={handleOpenMail}
            disabled={busy || !to}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Mail'i Aç
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
